import {
  AttachMoney,
  CalendarToday,
  Delete,
  Description,
  DirectionsCar,
  ListAlt,
  LocalGasStation,
  LocationOn,
  Notes as NotesIcon,
  Phone,
  PhotoAlbumOutlined,
  Timeline,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Switch,
  Typography,
} from '@mui/material'
import { Vehicle } from '@otw/models/inventory'
import {
  boxTruck as boxTruckIcon,
  cargoVan as cargoVanIcon,
  cutaway as cutawayIcon,
  stepVan as stepVanIcon,
} from 'assets'
import { useAuth } from 'contexts/AuthContext'
import { generateOpticStockNumber } from 'core/inventory/generateStockNumber'
import {
  formatDateTime,
  formatDistance,
  formatPhoneNumber,
  formatPrice,
  getVehicleStatusStyles,
} from 'core/utils/inventoryUtils'
import { useInventory } from 'hooks/inventory/useInventory'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { usePhotosForInventoryObject } from '@/hooks/inventory/usePhotosForInventoryObject'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import VehicleForm from '@/components/inventory/forms/VehicleForm'
import { MobileRecordCard, type MobileRecordCardChip } from 'components/MobileRecordCard'
import { VehicleRecordSheet } from './VehicleRecordSheet'
import { vehicleDealQualityTone, vehicleInventoryStatusTone } from '../vehicleInventoryStatusTone'
import { getDealQualityInfo } from 'hooks/graybook'
import type { VehicleDealMetrics } from 'hooks/graybook/calculateDealForVehicle'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { buildVehicleRecordFields } from '../buildVehicleRecordFields'
import {
  buildFundedUpdate,
  buildPaidForUpdate,
} from '@/components/inventory/utils/vehicleFinancialToggleUpdates'

const cachedIcons = {
  'step van': stepVanIcon,
  'box truck': boxTruckIcon,
  'cargo van': cargoVanIcon,
  cutaway: cutawayIcon,
  default: stepVanIcon,
}

Object.values(cachedIcons).forEach(src => {
  const img = new Image()
  img.src = src
})

export type InventoryCardSurface = 'edit' | 'notes' | 'fields'

interface VehicleCardProps {
  collectionName: 'master_inventory' | 'shield_inventory'
  vehicle: Vehicle
  onEditVehicle: (data: { [key: string]: any }) => void
  onDeleteClick: () => void
  onManagePhotosClick: () => void
  onManageDocumentsClick: () => void
  onOpenTimelineClick: () => void
  onShowHideOnWebsiteClick: () => void
  isAdmin?: boolean
  vehicles: Vehicle[]
  canModify?: boolean
  canViewPurchasePrice?: boolean
  canViewProfit?: boolean
  canViewProfitRealized?: boolean
  deal?: VehicleDealMetrics | null
  activeSurface?: InventoryCardSurface | null
  onActiveSurfaceChange?: (surface: InventoryCardSurface | null) => void
}

const VehicleCard: React.FC<VehicleCardProps> = ({
  collectionName,
  vehicle,
  onEditVehicle,
  onDeleteClick,
  onManagePhotosClick,
  onManageDocumentsClick,
  onOpenTimelineClick,
  onShowHideOnWebsiteClick,
  isAdmin,
  vehicles,
  canModify = false,
  canViewPurchasePrice = false,
  canViewProfit = false,
  canViewProfitRealized = false,
  deal = null,
  activeSurface = null,
  onActiveSurfaceChange,
}) => {
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar()
  const { userInfo } = useAuth()
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false)
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = React.useState(false)
  const [optimisticShowOnWebsite, setOptimisticShowOnWebsite] = React.useState(vehicle.show_on_website)
  const [optimisticProfitRealized, setOptimisticProfitRealized] = React.useState(vehicle.profit_realized === true)
  const [optimisticPaidFor, setOptimisticPaidFor] = React.useState(vehicle.paid_for === true)
  const [optimisticFunded, setOptimisticFunded] = React.useState(vehicle.funded === true)
  const [localSurface, setLocalSurface] = useState<InventoryCardSurface | null>(null)
  const surface = onActiveSurfaceChange ? activeSurface : localSurface
  const setSurface = useCallback(
    (next: InventoryCardSurface | null) => {
      if (onActiveSurfaceChange) onActiveSurfaceChange(next)
      else setLocalSurface(next)
    },
    [onActiveSurfaceChange],
  )
  const isMobile = useIsMobile()

  useEffect(() => {
    setOptimisticShowOnWebsite(vehicle.show_on_website)
  }, [vehicle.show_on_website])

  useEffect(() => {
    setOptimisticProfitRealized(vehicle.profit_realized === true)
  }, [vehicle.profit_realized])

  useEffect(() => {
    setOptimisticPaidFor(vehicle.paid_for === true)
  }, [vehicle.paid_for])

  useEffect(() => {
    setOptimisticFunded(vehicle.funded === true)
  }, [vehicle.funded])

  const handleProfitRealizedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (!canModify) {
      showWarning("You don't have permission to modify inventory")
      return
    }
    const newValue = event.target.checked
    const previousValue = optimisticProfitRealized
    setOptimisticProfitRealized(newValue)
    try {
      onEditVehicle({ profit_realized: newValue })
    } catch (error) {
      console.error('Error updating profit_realized:', error)
      setOptimisticProfitRealized(previousValue)
    }
  }

  const handlePaidForChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (!canModify) {
      showWarning("You don't have permission to modify inventory")
      return
    }
    const newValue = event.target.checked
    const previousValue = optimisticPaidFor
    setOptimisticPaidFor(newValue)
    try {
      const updateData = buildPaidForUpdate(
        vehicle,
        newValue,
        userInfo?.email || userInfo?.id,
      )
      await onEditVehicle(updateData)
    } catch (error) {
      console.error('Error updating paid_for:', error)
      setOptimisticPaidFor(previousValue)
    }
  }

  const handleFundedChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (!canModify) {
      showWarning("You don't have permission to modify inventory")
      return
    }
    const newValue = event.target.checked
    const previousValue = optimisticFunded
    setOptimisticFunded(newValue)
    try {
      await onEditVehicle(buildFundedUpdate(newValue))
    } catch (error) {
      console.error('Error updating funded:', error)
      setOptimisticFunded(previousValue)
    }
  }

  const FinancialToggleRow = ({
    label,
    checked,
    onChange,
  }: {
    label: string
    checked: boolean
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  }) => (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Typography variant='body2' sx={{ fontSize: '0.75rem', fontWeight: 'medium' }}>
        {label}:
      </Typography>
      <Switch checked={checked} onChange={onChange} color='primary' size='small' disabled={!canModify} />
    </Box>
  )

  const { data: photos, isLoading: isLoadingPhoto } = usePhotosForInventoryObject(collectionName, vehicle.id, {
    onlyMainPhoto: true,
    enabled: vehicle.photo_storage_path !== '',
  })

  const handleShowHideClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShowConfirmDialog(true)
  }

  const handleConfirmShowHide = () => {
    setOptimisticShowOnWebsite(!optimisticShowOnWebsite)
    onShowHideOnWebsiteClick()
    setShowConfirmDialog(false)
  }

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShowDeleteConfirmDialog(true)
  }

  const handleConfirmDelete = () => {
    onDeleteClick()
    setShowDeleteConfirmDialog(false)
  }

  const handleClickVehicle = useCallback(
    (vehicle: Vehicle) => {
      if (!canModify) {
        // Show a message when user without permission tries to edit
        showWarning("You don't have permission to modify inventory")
        return
      }
      setSurface('edit')
    },
    [canModify, showWarning, setSurface],
  )

  const statusColor = useMemo(() => {
    return getVehicleStatusStyles(vehicle.status).hex
  }, [vehicle.status])

  const dealQualityInfo = useMemo(() => {
    const quality = deal?.dealQuality ?? vehicle.dealQuality
    if (!quality) return null
    const { label, color } = getDealQualityInfo(quality)
    return { label, color }
  }, [deal?.dealQuality, vehicle.dealQuality])

  const recordFields = useMemo(
    () =>
      buildVehicleRecordFields({
        vehicle,
        deal,
        canViewPurchasePrice,
        canViewProfit,
        canViewProfitRealized,
      }),
    [vehicle, deal, canViewPurchasePrice, canViewProfit, canViewProfitRealized],
  )

  const mobileFlagChips = useMemo((): MobileRecordCardChip[] => {
    const chips: MobileRecordCardChip[] = []
    if (dealQualityInfo) {
      chips.push({
        label: dealQualityInfo.label,
        tone: vehicleDealQualityTone(deal?.dealQuality ?? vehicle.dealQuality),
      })
    }
    chips.push({
      label: optimisticShowOnWebsite ? 'On website' : 'Off website',
      tone: optimisticShowOnWebsite ? 'success' : 'neutral',
    })
    if (optimisticPaidFor) chips.push({ label: 'Paid', tone: 'success' })
    if (optimisticFunded) chips.push({ label: 'Funded', tone: 'info' })
    if (vehicle.mechanical_status) {
      chips.push({ label: `Mech ${vehicle.mechanical_status}`, tone: 'neutral' })
    }
    if (vehicle.body_status) {
      chips.push({ label: `Body ${vehicle.body_status}`, tone: 'neutral' })
    }
    return chips
  }, [
    deal?.dealQuality,
    dealQualityInfo,
    optimisticFunded,
    optimisticPaidFor,
    optimisticShowOnWebsite,
    vehicle.body_status,
    vehicle.dealQuality,
    vehicle.mechanical_status,
  ])

  const handleEditVehicle = async (data: { [key: string]: any }) => {
    await onEditVehicle(data)
    setSurface(null)
  }

  const VehiclePhoto = () => {
    if (isLoadingPhoto) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress sx={{ mt: 2 }} />
        </Box>
      )
    }

    const iconSrc = cachedIcons[vehicle?.type?.toLowerCase() as keyof typeof cachedIcons] || cachedIcons.default

    return (
      <>
        {photos && photos.length > 0 ? (
          <CardMedia
            component='img'
            srcSet={`${photos[0].url}?w=82&h=82&fit=crop&auto=format&dpr=2 2x`}
            src={`${photos[0].url}?w=82&h=82&fit=crop&auto=format`}
            alt={[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
            loading='lazy'
            sx={{
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            height='100%'
            sx={{ bgcolor: 'background.default' }}
          >
            <div
              title='No Photo Available'
              style={{
                width: '100%',
                height: '100%',
                backgroundImage: `url(${iconSrc})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '2px',
                mixBlendMode: 'multiply',
              }}
              role='img'
              aria-label={`${vehicle?.type || 'Default'} vehicle`}
            ></div>
          </Box>
        )}
      </>
    )
  }

  const mobileUnitTitle = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Truck'
  const mobileListPrice =
    vehicle.optic_list_price != null ? formatPrice(vehicle.optic_list_price) : 'N/A'

  const MobileVehicleCard = () => {
    const priceDetail = canViewPurchasePrice
      ? `Buy ${formatPrice(vehicle.seller_asking_price)} · ${formatDistance(vehicle.miles)} mi`
      : `${formatDistance(vehicle.miles)} mi · ${vehicle.location || '—'}`

    return (
      <Card
        sx={{
          mb: 0.5,
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
          <Box
            sx={{
              width: 88,
              minHeight: 88,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
            }}
          >
            <VehiclePhoto />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MobileRecordCard
              accent='navy'
              title={mobileUnitTitle}
              meta={`Stock # ${vehicle.stock_number || '—'}`}
              detail={priceDetail}
              status={
                vehicle.status
                  ? { tone: vehicleInventoryStatusTone(vehicle.status), label: vehicle.status }
                  : undefined
              }
              chips={mobileFlagChips}
              maxChips={4}
              trailing={
                <Typography variant='subtitle1' fontWeight={700} color='success.dark' sx={{ fontSize: '1rem' }}>
                  {mobileListPrice}
                </Typography>
              }
              onClick={canModify ? () => handleClickVehicle(vehicle) : undefined}
            />
          </Box>
        </Box>

        <Box
          sx={{ px: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {canViewProfit && vehicle.seller_asking_price != null && Number(vehicle.profit) !== 0 && (
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: Number(vehicle.profit) > 0 ? 'success.dark' : 'error.main',
              }}
            >
              Profit: {formatPrice(vehicle.profit)}
            </Typography>
          )}
          {canViewProfitRealized && (
            <FinancialToggleRow
              label='Profit realized'
              checked={optimisticProfitRealized}
              onChange={handleProfitRealizedChange}
            />
          )}
          <FinancialToggleRow label='Paid for' checked={optimisticPaidFor} onChange={handlePaidForChange} />
          <FinancialToggleRow label='Funded' checked={optimisticFunded} onChange={handleFundedChange} />
        </Box>

        <CardActions sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', px: 1, py: 0.5 }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
            <IconButton
              aria-label='Delete Vehicle'
              onClick={handleDeleteClick}
              disabled={!canModify}
              size='small'
            >
              <Delete fontSize='small' />
            </IconButton>
            <IconButton
              aria-label='View truck notes'
              onClick={event => {
                event.stopPropagation()
                setSurface('notes')
              }}
              size='small'
            >
              <NotesIcon fontSize='small' />
            </IconButton>
            <IconButton
              aria-label='Manage Photos'
              onClick={event => {
                event.stopPropagation()
                onManagePhotosClick()
              }}
              disabled={!canModify || !userInfo?.email_verified}
              size='small'
            >
              <PhotoAlbumOutlined fontSize='small' />
            </IconButton>
            <IconButton
              aria-label='Manage Documents'
              onClick={event => {
                event.stopPropagation()
                onManageDocumentsClick()
              }}
              size='small'
            >
              <Description fontSize='small' />
            </IconButton>
            <IconButton
              aria-label='View Timeline'
              onClick={event => {
                event.stopPropagation()
                onOpenTimelineClick()
              }}
              size='small'
            >
              <Timeline fontSize='small' />
            </IconButton>
            <IconButton
              aria-label='All vehicle fields'
              onClick={event => {
                event.stopPropagation()
                setSurface('fields')
              }}
              size='small'
            >
              <ListAlt fontSize='small' />
            </IconButton>
            <Switch
              checked={optimisticShowOnWebsite}
              icon={<VisibilityOff />}
              checkedIcon={<Visibility />}
              onClick={handleShowHideClick}
              color={optimisticShowOnWebsite ? 'success' : 'error'}
              disabled={!canModify}
              size='small'
            />
          </Box>
        </CardActions>
      </Card>
    )
  }

  return (
    <>
      {isMobile ? (
        <MobileVehicleCard />
      ) : (
        <Card
          sx={{
            minWidth: 275,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            '&:hover': canModify ? { boxShadow: 6 } : {},
            color: 'text.primary',
            cursor: canModify ? 'pointer' : 'default',
          }}
          onClick={() => (canModify ? handleClickVehicle(vehicle) : null)}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: '2px solid',
              borderColor: 'primary.light',
              opacity: 0,
              transition: 'opacity 0.3s',
              '&:hover': { opacity: 1 },
            }}
          />
          <CardHeader
            sx={{ flexShrink: 0 }}
            title={
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography
                  variant='h6'
                  sx={{
                    lineHeight: 1.2,
                    minHeight: '2.4em',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {[vehicle.year, vehicle.make, vehicle.model, vehicle.size].filter(Boolean).join(' ')}
                </Typography>
              </Box>
            }
            subheader={
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography variant='body2'>Stock Number: {vehicle.stock_number}</Typography>
              </Box>
            }
          />
          <Box sx={{ height: 200, flexShrink: 0, position: 'relative' }}>
            <VehiclePhoto />
            {vehicle.status && (
              <Typography
                variant='caption'
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  color: 'white',
                  backgroundColor: statusColor,
                  fontWeight: 'medium',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  opacity: 0.9,
                }}
              >
                {vehicle.status}
              </Typography>
            )}
          </Box>
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2} sx={{ height: '100%' }}>
              <Grid item xs={6}>
                <Typography variant='subtitle1' display='flex' alignItems='center' gap={1}>
                  <DirectionsCar fontSize='small' /> Vehicle Info
                </Typography>
                <Typography variant='body2'>
                  <strong>VIN:</strong> {vehicle.vin}
                </Typography>
                <Typography variant='body2'>
                  <strong>Stock #:</strong> {vehicle.stock_number}
                </Typography>
                <Typography variant='body2'>
                  <strong>Type:</strong> {vehicle.type}
                </Typography>
                <Typography variant='body2'>
                  <strong>Size:</strong> {vehicle.size}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant='subtitle1' display='flex' alignItems='center' gap={1}>
                  <LocalGasStation fontSize='small' /> Performance
                </Typography>
                <Typography variant='body2'>
                  <strong>Fuel:</strong> {vehicle.fuel}
                </Typography>
                <Typography variant='body2'>
                  <strong>Miles:</strong> {formatDistance(vehicle.miles)}
                </Typography>
                <Typography variant='body2'>
                  <strong>Deal Quality:</strong>{' '}
                  {dealQualityInfo && (
                    <Chip size='small' color={dealQualityInfo.color} label={dealQualityInfo.label}></Chip>
                  )}
                </Typography>
              </Grid>
              {canViewPurchasePrice && (
                <Grid item xs={6}>
                  <Typography variant='subtitle1' display='flex' alignItems='center' gap={1}>
                    <Phone fontSize='small' /> Contact
                  </Typography>
                  <Typography variant='body2'>
                    <strong>Contact:</strong> {vehicle.who}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>Location:</strong> {vehicle.location}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant='subtitle1' display='flex' alignItems='center' gap={1}>
                  <AttachMoney fontSize='small' /> Pricing
                </Typography>
                {canViewPurchasePrice && (
                  <Typography variant='body2'>
                    <strong>Buy Price:</strong>{' '}
                    {vehicle.seller_asking_price != null ? formatPrice(vehicle.seller_asking_price) : 'N/A'}
                  </Typography>
                )}
                <Typography variant='body2'>
                  <strong>Sell Price:</strong>{' '}
                  {vehicle.optic_list_price != null ? formatPrice(vehicle.optic_list_price) : 'N/A'}
                </Typography>
                {canViewProfit && (
                  <Typography variant='body2'>
                    <strong>Potential Profit:</strong> {vehicle.profit != null ? formatPrice(vehicle.profit) : 'N/A'}
                  </Typography>
                )}
                {canViewProfitRealized && (
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={1}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Typography variant='body2'>
                      <strong>Profit Realized:</strong>
                    </Typography>
                    <Switch
                      checked={optimisticProfitRealized}
                      onChange={handleProfitRealizedChange}
                      color='primary'
                      size='small'
                      disabled={!canModify}
                    />
                  </Box>
                )}
                <Box
                  display='flex'
                  alignItems='center'
                  gap={1}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Typography variant='body2'>
                    <strong>Paid for:</strong>
                  </Typography>
                  <Switch
                    checked={optimisticPaidFor}
                    onChange={handlePaidForChange}
                    color='primary'
                    size='small'
                    disabled={!canModify}
                  />
                </Box>
                <Box
                  display='flex'
                  alignItems='center'
                  gap={1}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Typography variant='body2'>
                    <strong>Funded:</strong>
                  </Typography>
                  <Switch
                    checked={optimisticFunded}
                    onChange={handleFundedChange}
                    color='primary'
                    size='small'
                    disabled={!canModify}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display='flex' alignItems='center' gap={2}>
                  <Typography variant='body2' display='flex' alignItems='center' gap={1}>
                    <CalendarToday fontSize='small' /> <strong>Added:</strong> {formatDateTime(vehicle.date_added)}
                  </Typography>
                  {vehicle.sold_date && (
                    <Typography variant='body2' display='flex' alignItems='center' gap={1}>
                      <CalendarToday fontSize='small' /> <strong>Sold:</strong>{' '}
                      {formatDateTime(vehicle.sold_date) || 'N/A'}
                    </Typography>
                  )}
                  {vehicle.status === 'Pending Available' && vehicle.date_available && (
                    <Typography variant='body2' display='flex' alignItems='center' gap={1}>
                      <CalendarToday fontSize='small' /> <strong>Available:</strong>{' '}
                      {formatDateTime(vehicle.date_available)}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}>
            <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
              <IconButton
                aria-label='Delete Vehicle'
                onClick={handleDeleteClick}
                disabled={!canModify}
              >
                <Delete />
              </IconButton>
              <IconButton
                aria-label='View truck notes'
                onClick={event => {
                  event.stopPropagation()
                  setSurface('notes')
                }}
              >
                <NotesIcon />
              </IconButton>
              <IconButton
                aria-label='Manage Photos'
                onClick={event => {
                  event.stopPropagation()
                  onManagePhotosClick()
                }}
                disabled={!canModify || !userInfo?.email_verified}
              >
                <PhotoAlbumOutlined />
              </IconButton>
              <IconButton
                aria-label='Manage Documents'
                onClick={event => {
                  event.stopPropagation()
                  onManageDocumentsClick()
                }}
              >
                <Description />
              </IconButton>
              <IconButton
                aria-label='View Timeline'
                onClick={event => {
                  event.stopPropagation()
                  onOpenTimelineClick()
                }}
              >
                <Timeline />
              </IconButton>
              <IconButton
                aria-label='All vehicle fields'
                onClick={event => {
                  event.stopPropagation()
                  setSurface('fields')
                }}
              >
                <ListAlt />
              </IconButton>
              <Switch
                checked={optimisticShowOnWebsite}
                icon={<VisibilityOff />}
                checkedIcon={<Visibility />}
                onClick={handleShowHideClick}
                color={optimisticShowOnWebsite ? 'success' : 'error'}
                disabled={!canModify}
              />
            </Box>
          </CardActions>
        </Card>
      )}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <DialogTitle>{[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}</DialogTitle>
        <DialogContent>
          Are you sure you want to {optimisticShowOnWebsite ? 'hide' : 'show'} this vehicle
          {optimisticShowOnWebsite ? ' from' : ' on'} the website?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmShowHide} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={surface === 'notes'}
        onClose={() => setSurface(null)}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Notes for {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}</DialogTitle>
        <DialogContent>
          <Typography variant='body1' whiteSpace='pre-wrap'>
            {vehicle.truck_notes || 'No notes available'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSurface(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showDeleteConfirmDialog}
        onClose={() => setShowDeleteConfirmDialog(false)}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <DialogTitle>Delete Vehicle</DialogTitle>
        <DialogContent>
          <Typography variant='body1'>
            Are you sure you want to delete <strong>{[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}</strong>?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color='error' variant='contained' autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <VehicleForm
        open={surface === 'edit' && !onActiveSurfaceChange}
        vehicle={vehicle}
        onClose={() => {
          setSurface(null)
        }}
        onSubmit={handleEditVehicle}
        inventoryCollection={collectionName}
        generateStockNumber={() =>
          generateOpticStockNumber(
            (vehicles as Vehicle[])?.map(vehicle => vehicle.stock_number) || [],
            userInfo?.first_name || '',
            userInfo?.last_name || '',
          )
        }
        isEdit={true}
      />
      <VehicleRecordSheet
        open={surface === 'fields'}
        onClose={() => setSurface(null)}
        title={[vehicle.year, vehicle.make, vehicle.model, vehicle.stock_number].filter(Boolean).join(' ')}
        fields={recordFields}
      />
    </>
  )
}

export default VehicleCard
